import { useQuery } from '@tanstack/react-query';
import { useEffect, useReducer, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  indeedSettingsQueryOptions,
  useUpdateIndeedSettings,
} from '@/hooks/use-indeed-settings';
import { useSourceIndeedContract } from '@/hooks/use-source-indeed-contract';
import {
  type HeldIndeedToggles,
  takeHeldToggles,
} from '@/utils/indeed-connect-storage';

export type IndeedConnectOutcome =
  | 'connected'
  | 'connected_settings_failed'
  | 'not_connected';

type Phase =
  | 'idle'
  | 'waiting'
  | 'applying'
  | 'applied'
  | 'not_connected'
  | 'discarded';
type Attempt = {
  startedAt: number;
  held: HeldIndeedToggles | null;
  deadline: number;
};
type State = { phase: Phase; attempt: Attempt | null };
type Action = { phase: Phase; attempt: Attempt };

function reducer(state: State, action: Action): State {
  if (state.attempt && state.attempt.startedAt !== action.attempt.startedAt)
    return state;
  return action;
}

export function useIndeedConnectReturn(
  onOutcome: (outcome: IndeedConnectOutcome) => void,
) {
  const enabled = useSourceIndeedContract();
  const [params, setParams] = useSearchParams();
  const [state, dispatch] = useReducer(reducer, {
    phase: 'idle',
    attempt: null,
  });
  const initialized = useRef(false);
  const active = useRef(false);
  const applying = useRef<number | null>(null);
  const outcome = useRef(onOutcome);
  outcome.current = onOutcome;
  const { mutateAsync } = useUpdateIndeedSettings();
  const query = useQuery({
    ...indeedSettingsQueryOptions,
    enabled: enabled && state.phase === 'waiting',
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: state.phase === 'waiting' ? 2000 : false,
    retry: false,
  });

  useEffect(() => {
    active.current = enabled;
    return () => {
      active.current = false;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || initialized.current) return;
    initialized.current = true;
    const result = params.get('indeed_connect');
    if (result !== null) {
      const next = new URLSearchParams(params);
      next.delete('indeed_connect');
      setParams(next, { replace: true });
    }
    const now = Date.now();
    const held = takeHeldToggles(now);
    const attempt = {
      startedAt: held?.startedAt ?? now,
      held,
      deadline: now + 30_000,
    };
    if (result === 'connected') {
      dispatch({ phase: 'waiting', attempt });
    } else if (result === 'cancelled') {
      dispatch({ phase: 'not_connected', attempt });
      outcome.current('not_connected');
    } else if (held) {
      dispatch({ phase: 'discarded', attempt });
    }
  }, [enabled, params, setParams]);

  useEffect(() => {
    if (!enabled || state.phase !== 'waiting' || !state.attempt) return;
    const attempt = state.attempt;
    const timeout = setTimeout(
      () => {
        dispatch({ phase: 'not_connected', attempt });
        outcome.current('not_connected');
      },
      Math.max(0, attempt.deadline - Date.now()),
    );
    return () => clearTimeout(timeout);
  }, [enabled, state]);

  useEffect(() => {
    const attempt = state.attempt;
    if (
      !enabled ||
      state.phase !== 'waiting' ||
      !attempt ||
      applying.current === attempt.startedAt ||
      !query.isFetchedAfterMount ||
      query.isFetching ||
      query.isError ||
      !query.data?.indeed_authorized ||
      Date.now() >= attempt.deadline
    )
      return;
    applying.current = attempt.startedAt;
    dispatch({ phase: 'applying', attempt });
    const finish = (result: IndeedConnectOutcome) => {
      if (!active.current || applying.current !== attempt.startedAt) return;
      dispatch({ phase: 'applied', attempt });
      outcome.current(result);
    };
    const changed = attempt.held?.changed ?? {};
    if (Object.keys(changed).length === 0) {
      finish('connected');
      return;
    }
    void mutateAsync(changed).then(
      () => finish('connected'),
      () => finish('connected_settings_failed'),
    );
  }, [
    enabled,
    state,
    query.isFetchedAfterMount,
    query.isFetching,
    query.isError,
    query.data,
    mutateAsync,
  ]);

  return state.phase;
}
